class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.hero-slideshow__track');
    this.slides = Array.from(this.querySelectorAll('.hero-slideshow__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero-slideshow__dot'));
    this.prevBtn = this.querySelector('.hero-slideshow__arrow--prev');
    this.nextBtn = this.querySelector('.hero-slideshow__arrow--next');
    this.playPauseBtn = this.querySelector('.hero-slideshow__playpause');
    this.viewport = this.querySelector('.hero-slideshow__viewport');

    if (!this.track || this.slides.length === 0) return;

    this.currentIndex = 0;
    this.isPlaying = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.PAUSE_ICON =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 4h3v16H7zM14 4h3v16h-3z" fill="currentColor"/></svg>';
    this.PLAY_ICON =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 4l13 8-13 8z" fill="currentColor"/></svg>';

    this.bindEvents();
    this.observeSlides();
    this.setActive(0);
    this.updatePlayPauseUI();

    this._onResize = this.debounce(() => this.syncHeight(), 150);
    window.addEventListener('resize', this._onResize);

    if (this.isPlaying && !this.reducedMotion) this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    window.removeEventListener('resize', this._onResize);
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    this.playPauseBtn?.addEventListener('click', () => this.toggleAutoplay());

    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.currentIndex + 1);
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.currentIndex - 1);
      }
    });

    // Pause on hover/focus so an interacting visitor never fights the carousel.
    this.addEventListener('mouseenter', () => this.pauseForInteraction());
    this.addEventListener('mouseleave', () => this.resumeAfterInteraction());
    this.addEventListener('focusin', () => this.pauseForInteraction());
    this.addEventListener('focusout', () => this.resumeAfterInteraction());

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay();
      else if (this.isPlaying) this.startAutoplay();
    });
  }

  observeSlides() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            this.setActive(this.slides.indexOf(entry.target));
          }
        });
      },
      { root: this.track, threshold: [0.6] }
    );

    this.slides.forEach((slide) => observer.observe(slide));
  }

  syncHeight() {
    const activeSlide = this.slides[this.currentIndex];
      if (!this.viewport || !activeSlide) return;
      this.viewport.style.height = `${activeSlide.getBoundingClientRect().height}px`;
  }

  debounce(fn, wait) {
    let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
    };
  }

  setActive(index) {
    this.currentIndex = index;
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-current', i === index ? 'true' : 'false');
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === index) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    this.syncHeight();
  }

  goTo(index) {
    const max = this.slides.length - 1;
    const target = Math.max(0, Math.min(index, max));
    this.slides[target].scrollIntoView({
      behavior: this.reducedMotion ? 'auto' : 'smooth',
      inline: 'start',
      block: 'nearest',
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      const next = (this.currentIndex + 1) % this.slides.length;
      this.goTo(next);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    clearInterval(this.autoplayTimer);
  }

  // Hover/focus pause respects current play/pause state set by the merchant's visitor.
  pauseForInteraction() {
    this.stopAutoplay();
  }

  resumeAfterInteraction() {
    if (this.isPlaying && !this.reducedMotion) this.startAutoplay();
  }

  toggleAutoplay() {
    this.isPlaying = !this.isPlaying;
    if (this.isPlaying) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }
    this.updatePlayPauseUI();
  }

  updatePlayPauseUI() {
    if (!this.playPauseBtn) return;
    this.playPauseBtn.innerHTML = this.isPlaying ? this.PAUSE_ICON : this.PLAY_ICON;
    this.playPauseBtn.setAttribute('aria-pressed', this.isPlaying ? 'true' : 'false');
    this.playPauseBtn.setAttribute(
      'aria-label',
      this.isPlaying ? 'Pause slideshow' : 'Play slideshow'
    );
  }
}

customElements.define('hero-slideshow', HeroSlideshow);
